import { useEffect, useMemo, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import { Navigate } from 'react-router-dom'

import { getFnbAnalytics, getOrgEventsForReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { EventSelector } from '../EventSelector'
import { EmptyContainer } from '../EmptyContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import FnbAnalyticsItemTable from './FnbAnalyticsItemTable'
import FnbAnalyticsRecommendations from './FnbAnalyticsRecommendations'
import FnbAnalyticsSectionTable from './FnbAnalyticsSectionTable'
import FnbAnalyticsSummary from './FnbAnalyticsSummary'

import './fnbAnalyticsWrapper.scss'

export default function FnbAnalyticsWrapper({ organization }) {
  const [events, setEvents] = useState([])
  const [eventUUID, setEventUUID] = useState('')
  const [reportData, setReportData] = useState(null)
  const [isLoadingEvents, setIsLoadingEvents] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingReport)
  const [loadError, setLoadError] = useState(null)

  const organizationUUID = organization?.uuid
  const fnbEnabled = Boolean(organization?.setting?.foodAndBeverage)

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingEvents(true)

    getOrgEventsForReport(organizationUUID)
      .then((res) => {
        if (cancelled) return
        setEvents(Array.isArray(res?.data) ? res.data : [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load events', err)
        setLoadError('Could not load events for this organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEvents(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!organizationUUID || !eventUUID) {
      setReportData(null)
      return
    }

    let cancelled = false
    setIsLoadingReport(true)
    setLoadError(null)

    getFnbAnalytics(organizationUUID, eventUUID)
      .then((res) => {
        if (cancelled) return
        setReportData(res?.data ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load F&B analytics', err)
        setLoadError('Could not load F&B analytics for this event.')
        setReportData(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingReport(false)
          completeRefresh()
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID, eventUUID, refreshToken])

  const selectedEvent = useMemo(
    () => events.find((event) => event.uuid === eventUUID) || null,
    [events, eventUUID]
  )

  if (!fnbEnabled) {
    return <Navigate to="/settings/organization-information" replace />
  }

  const isLoading = isLoadingEvents || isLoadingReport
  const hasData =
    reportData &&
    reportData.error !== 'event_not_found' &&
    reportData.error !== 'fnb_not_enabled'

  return (
    <section
      className="spacer-sm fnb-analytics-wrapper"
      id="fnb-analytics-wrapper"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>F&amp;B Analytics</h1>
        </div>
        <p className="section-header-desc">
          Review food &amp; beverage revenue, item mix, section conversion, and
          recommendations for {organization?.name || 'your organization'}.
        </p>
      </header>

      <Card body className="card--md mb-4">
        <Stack
          direction="horizontal"
          className="flex-wrap gap-3 align-items-end"
        >
          <div className="flex-grow-1">
            <EventSelector
              events={events}
              selected={eventUUID}
              setSelected={setEventUUID}
              isLoading={isLoadingEvents}
            />
          </div>
          <ReportRefreshButton
            onRefresh={refresh}
            isRefreshing={isLoadingReport}
            disabled={!eventUUID || isLoadingReport}
          />
        </Stack>
      </Card>

      {loadError ? (
        <Card body className="card--md mb-4">
          <p className="text-danger mb-0">{loadError}</p>
        </Card>
      ) : null}

      {!eventUUID ? (
        <EmptyContainer>
          <p className="mb-0">Select an event to view F&amp;B analytics.</p>
        </EmptyContainer>
      ) : isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : reportData?.error === 'event_not_found' ? (
        <EmptyContainer>
          <p className="mb-0">Event not found for this organization.</p>
        </EmptyContainer>
      ) : hasData && !reportData.hasSnapshot ? (
        <EmptyContainer>
          <p className="mb-0">
            No F&amp;B snapshot data yet for{' '}
            <strong>{selectedEvent?.name || 'this event'}</strong>. Snapshots
            are generated when orders are placed and refreshed nightly.
          </p>
        </EmptyContainer>
      ) : hasData ? (
        <Stack gap={4}>
          <FnbAnalyticsSummary
            kpis={reportData.kpis}
            topItemName={reportData.topItemName}
            snapshotDate={reportData.snapshotDate}
          />

          <div>
            <h2 className="h5 mb-3">Recommendations</h2>
            <FnbAnalyticsRecommendations
              recommendations={reportData.recommendations}
            />
          </div>

          <FnbAnalyticsItemTable items={reportData.items} />
          <FnbAnalyticsSectionTable sections={reportData.sections} />
        </Stack>
      ) : null}
    </section>
  )
}
